/**
 * Hook for tracking highlights in read-only or display content
 * Use this for pages that show content but don't use the TextBlock editor
 */

import { useEffect, useRef } from 'react';
import { useTextHighlighting } from '@/providers/TextHighlightingProvider';
import { highlightedItemsTracker } from '@/lib/services/HighlightedItemsTracker';
import { HighlightItem } from '@/lib/services/TextHighLightingService';

interface UseContentHighlightingOptions {
  /**
   * Enable/disable highlighting tracking
   * @default true
   */
  enabled?: boolean;
  
  /**
   * Debounce time in milliseconds before processing content
   * @default 300
   */
  debounceMs?: number;
}

/**
 * Hook to track highlights in content for display in the Structure Sidebar
 * This processes text content and updates the highlightedItemsTracker
 * 
 * @example
 * ```tsx
 * const { trackContent } = useContentHighlighting();
 * 
 * useEffect(() => {
 *   trackContent(myContent);
 * }, [myContent]);
 * ```
 */
export const useContentHighlighting = (options: UseContentHighlightingOptions = {}) => {
  const { enabled = true, debounceMs = 300 } = options;
  const { highlightItems } = useTextHighlighting();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Escape regex special characters
   */
  const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  /**
   * Process a single text string and find all highlight matches
   */
  const processText = (text: string): HighlightItem[] => {
    if (!text || !highlightItems.length) {
      return [];
    }

    const foundItems: HighlightItem[] = [];
    const foundIds = new Set<string>();

    highlightItems.forEach(item => {
      item.keys.forEach(key => {
        // Create regex pattern based on whether it's a multi-word phrase or single word
        let regex: RegExp;
        if (key.includes(' ')) {
          // Multi-word phrase - use lookahead/lookbehind
          const escapedKey = escapeRegExp(key);
          regex = new RegExp(`(?<!\\w)${escapedKey}(?!\\w)`, 'gi');
        } else {
          // Single word - use word boundaries
          const escapedKey = escapeRegExp(key);
          regex = new RegExp(`\\b${escapedKey}\\b`, 'gi');
        }

        // Check if this item matches in the text
        if (regex.test(text) && !foundIds.has(item.id)) {
          foundItems.push(item);
          foundIds.add(item.id);
        }
      });
    });

    return foundItems;
  };

  /**
   * Extract text from HTML string
   */
  const extractTextFromHTML = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  /**
   * Track highlights in content
   * Accepts either plain text or HTML string
   * 
   * @param content - Text or HTML content to process
   */
  const trackContent = (content: string | string[] | null | undefined) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Always use setTimeout to avoid updating during render
    timeoutRef.current = setTimeout(() => {
      if (!enabled || !content) {
        highlightedItemsTracker.updateTrackedItems([]);
        return;
      }
      try {
        // Handle array of content strings
        const contentArray = Array.isArray(content) ? content : [content];
        
        // Combine all content and extract text
        const combinedText = contentArray
          .filter(Boolean)
          .map(c => {
            // Check if content looks like HTML
            if (c.includes('<') && c.includes('>')) {
              return extractTextFromHTML(c);
            }
            return c;
          })
          .join(' ');

        // Process the text and find matches
        const foundItems = processText(combinedText);

        // Update the tracker
        highlightedItemsTracker.updateTrackedItems(foundItems);
      } catch (error) {
        console.error('Error processing content for highlights:', error);
        highlightedItemsTracker.updateTrackedItems([]);
      }
    }, debounceMs);
  };

  /**
   * Track highlights from a data object
   * Useful for processing form data or structured content
   * 
   * @param data - Object with string values to process
   * @param extractFields - Optional function to extract specific fields
   */
  const trackFromData = (
    data: any,
    extractFields?: (data: any) => string[]
  ) => {
    // Use setTimeout to avoid updating during render
    setTimeout(() => {
      if (!enabled || !data) {
        highlightedItemsTracker.updateTrackedItems([]);
        return;
      }

      try {
        let contentStrings: string[];

        if (extractFields) {
          // Use custom extraction function
          contentStrings = extractFields(data);
        } else {
          // Default: extract all string values from the object
          contentStrings = extractStringValues(data);
        }

        // Track the combined content (this will add its own debounce)
        trackContent(contentStrings);
      } catch (error) {
        console.error('Error processing data for highlights:', error);
        highlightedItemsTracker.updateTrackedItems([]);
      }
    }, 0);
  };

  /**
   * Recursively extract all string values from an object
   */
  const extractStringValues = (obj: any): string[] => {
    const strings: string[] = [];

    const extract = (value: any) => {
      if (typeof value === 'string') {
        strings.push(value);
      } else if (Array.isArray(value)) {
        value.forEach(extract);
      } else if (value && typeof value === 'object') {
        Object.values(value).forEach(extract);
      }
    };

    extract(obj);
    return strings;
  };

  /**
   * Clear all tracked highlights
   */
  const clearTracking = () => {
    // Use setTimeout to avoid updating during render
    setTimeout(() => {
      highlightedItemsTracker.updateTrackedItems([]);
    }, 0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Clear tracking when component unmounts (async to avoid updates during unmount)
      setTimeout(() => {
        highlightedItemsTracker.updateTrackedItems([]);
      }, 0);
    };
  }, []);

  return {
    trackContent,
    trackFromData,
    clearTracking,
    highlightItems
  };
};

